import React, { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import { getFirestore, collection, getDocs, orderBy, query } from 'firebase/firestore';
import { initializeApp } from 'firebase/app';

const firebaseConfig = {
  apiKey: "AIzaSyAGe4tEALPi6kdBnAJo4uf-8omOQ4Nda-0",
  authDomain: "pathway-academy.firebaseapp.com",
  projectId: "pathway-academy",
  storageBucket: "pathway-academy.firebasestorage.app",
  messagingSenderId: "822490806225",
  appId: "1:822490806225:web:218ebd85b334fa140312f0",
  measurementId: "G-43HW36L0TK"
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

const ScrollingGallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [images, setImages] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const containerRef = useRef(null);

  // Load images from Firestore
  useEffect(() => {
    const loadImages = async () => {
      try {
        const q = query(collection(db, 'gallery'), orderBy('order', 'asc'));
        const querySnapshot = await getDocs(q);
        const imageList = querySnapshot.docs
          .map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              src: data.downloadURL,
              alt: data.title || data.originalName,
              title: data.title || data.originalName,
              category: data.category,
              featuredOnHomepage: data.featuredOnHomepage || false
            };
          })
          .filter(img => img.featuredOnHomepage); // Only show featured images on homepage
        setImages(imageList);
        
        // Extract unique categories
        const uniqueCategories = [...new Set(imageList.map(img => img.category).filter(Boolean))];
        setCategories(uniqueCategories);
      } catch (error) {
        console.error('Error loading images from Firestore:', error);
      } finally {
        setLoading(false);
      }
    };

    loadImages();
  }, []);

  const filteredImages = selectedCategory === 'all' 
    ? images 
    : images.filter(img => img.category === selectedCategory);

  const getCategoryCount = (category) => {
    return category === 'all' 
      ? images.length 
      : images.filter(img => img.category === category).length;
  };

  // Handle lazy loading
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target.querySelector('img');
            if (img && img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
            }
          }
        });
      },
      {
        root: null,
        rootMargin: '200px',
        threshold: 0.1
      }
    );

    const elements = container.querySelectorAll('.gallery-item');
    elements.forEach(el => observer.observe(el));

    return () => {
      elements.forEach(el => observer.unobserve(el));
    };
  }, [filteredImages]);

  const openImage = (image) => {
    setSelectedImage(image);
    document.body.style.overflow = 'hidden';
  };

  const closeImage = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  };

  if (loading) {
    return (
      <section className="w-full bg-white py-16 overflow-hidden" id="gallery">
        <div className="container mx-auto px-6 mb-8">
          <h2 className="text-3xl font-medium text-center mb-8">Gallery</h2>
        </div>
        
        <div className="mt-8 sm:mt-12">
          <div className="-my-4 flex justify-center gap-5 overflow-x-auto py-4 sm:gap-8 hide-scrollbar pl-[10vw]">
            {[...Array(8)].map((_, index) => (
              <div
                key={index}
                className={`relative aspect-[9/10] w-44 flex-none 
                  overflow-hidden rounded-xl bg-gray-200 sm:w-72 sm:rounded-2xl 
                  animate-pulse ${index % 2 === 0 ? 'rotate-2' : '-rotate-2'}`}
              >
                <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="w-full bg-white py-16 overflow-hidden" id ="gallery">
      <div className="container mx-auto px-6 mb-8">
        <h2 className="text-3xl font-medium text-center mb-8">Gallery</h2>
        
        {/* Category Filters */}
        {categories.length > 0 && (
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-full font-medium transition-all ${
                selectedCategory === 'all'
                  ? 'bg-[#218581] text-white shadow-md'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              All ({getCategoryCount('all')})
            </button>
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full font-medium capitalize transition-all ${
                  selectedCategory === category
                    ? 'bg-[#218581] text-white shadow-md'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {category} ({getCategoryCount(category)})
              </button>
            ))}
          </div>
        )}
      </div>
      
      <div 
        ref={containerRef}
        className="mt-8 sm:mt-12"
      >
        <div className="-my-4 flex justify-center gap-5 overflow-x-auto py-4 sm:gap-8 hide-scrollbar pl-[10vw]">
          {filteredImages.map((image, index) => (
            <div
              key={image.id}
              className={`gallery-item relative aspect-[9/10] w-44 flex-none 
                overflow-hidden rounded-xl bg-zinc-100 sm:w-72 sm:rounded-2xl 
                cursor-pointer transform transition-all duration-300 
                hover:scale-105 ${index % 2 === 0 ? 'rotate-2' : '-rotate-2'}`}
              onClick={() => openImage(image)}
            >
              <img
                data-src={image.src}
                src="/api/placeholder/400/400"
                alt={image.alt}
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Full Screen Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center backdrop-blur-sm"
          onClick={closeImage}
        >
          <div 
            className="relative max-w-6xl w-full p-4 animate-scale-up"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeImage}
              className="absolute -top-12 right-4 text-white p-2 hover:text-gray-300 transition-colors"
            >
              <X size={24} />
            </button>
            <img
              src={selectedImage.src}
              alt={selectedImage.alt}
              className="w-full h-auto max-h-[90vh] object-contain rounded-lg"
            />
            {selectedImage.title && (
              <div className="absolute bottom-6 left-6 right-6 text-center">
                <p className="text-white text-lg bg-black/50 rounded-lg px-4 py-2 inline-block">
                  {selectedImage.title}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      <style jsx global>{`
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        @keyframes scale-up {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-scale-up {
          animation: scale-up 0.3s ease-out forwards;
        }
      `}</style>
    </section>
  );
};

export default ScrollingGallery;